import { getEnv, SERVER_BASE_URL } from "@swivu/env";
import { z, ZodError } from "zod";
import * as Sentry from "@sentry/browser";

export const HTTPMethod = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

declare global {
  interface ImportMetaEnv {
    readonly env: {
      ENV?: string;
    };
  }
}
export const HTTPStatusCode = [200] as const;

type SentryException = Parameters<typeof Sentry.captureException>;
// Wrapper to conditionally capture exceptions based on environment
const captureException = async (
  error: SentryException[0],
  context: Record<string, unknown>
) => {
  if (!["local", "development"].includes(getEnv())) {
    const user = await getUser(window.SERVER_BASE_URL ?? SERVER_BASE_URL);
    Sentry.captureException(error, { user, ...context });
  }
};

async function getUser(baseUrl: string) {
  try {
    const userRaw = await fetch(baseUrl + `/user/me`, {
      credentials: "include",
      method: "GET",

      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });
    const user = await userRaw.json();
    return {
      email: user.email,
      id: user.id,
      username: user.name
    };
  } catch {
    return {
      email: null,
      id: null,
      username: null
    };
  }
}
function urlWithParams<T extends Record<string, string>>(
  tokenUrl: string,
  body: T,
  method: (typeof HTTPMethod)[number]
): { url: string; params: Record<string, string> } {
  const remainingBody = { ...body };

  const boardsString: string[][] = [];
  if (
    method === "GET" &&
    "board_ids" in remainingBody &&
    Array.isArray(remainingBody.board_ids)
  ) {
    remainingBody?.board_ids.forEach((id) =>
      boardsString.push([`board_id[]`, id])
    );
    delete remainingBody.board_ids;
  }

  const url = tokenUrl.replace(/:\w+/g, (match) => {
    const key = match.slice(1);
    delete remainingBody[key];
    return encodeURIComponent(body[key]);
  });
  // convert reamining body object to array of arrays
  const remainingBodyArray = Object.entries(remainingBody);
  const params = new URLSearchParams([...boardsString, ...remainingBodyArray]);
  return {
    url:
      url +
      (method === "GET" && params.toString() ? `?${params.toString()}` : ""),
    params: remainingBody
  };
}

export const api = async <Request, Response>({
  method,
  url,
  requestSchema,
  responseSchema,
  body: bodyRaw,
  options = {}
}: {
  method: (typeof HTTPMethod)[number];
  url: string;
  requestSchema: z.ZodType<Request>;
  responseSchema: z.ZodType<Response>;
  body?: Request;
  options?: RequestInit & { mockData?: unknown };
}) => {
  try {
    const body = requestSchema.parse(bodyRaw);
    if (import.meta.env.VITE_USE_MOCKS === "true" && options?.mockData) {
      // const parsed = responseSchema.safeParse(options.mockData);
      const parsed = await responseSchema.parseAsync(options.mockData);
      return parsed;
    }

    const parsedUrl = urlWithParams(
      url,
      (body ?? {}) as Record<string, string>,
      method
    );

    const optionsWithDefault: RequestInit = {
      credentials: "include",
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: method === "GET" ? undefined : JSON.stringify(parsedUrl.params),
      ...options
    };

    const baseUrl = window.SERVER_BASE_URL ?? SERVER_BASE_URL;

    const responseRaw = await fetch(
      baseUrl + parsedUrl.url,
      optionsWithDefault
    );
    // catch a fetch error and throw the entire error

    if (!responseRaw.ok) {
      // check if error is in json
      const text = await responseRaw.text();
      const response = text ? JSON.parse(text) : null;
      // console.log("Server error response:", response);

      // Handle case where response is directly the error message string
      if (typeof response === "string") {
        const error = new Error(`${responseRaw.status}: ${response}`, {
          cause: response
        });

        void captureException(error, {
          tags: {
            api_endpoint: parsedUrl.url,
            http_status: responseRaw.status,
            http_method: method
          },
          extra: {
            request_body: bodyRaw,
            response: response
          }
        });
        throw error;
      }

      // Handle case where response is an error object
      if (response.error) {
        const error = new Error(
          `${responseRaw.status}: ${response.message}${
            response.error &&
            `\n\nError: ${JSON.stringify(response.error, null, 2)}`
          }`,
          { cause: response }
        );
        void captureException(error, {
          tags: {
            api_endpoint: parsedUrl.url,
            http_status: responseRaw.status,
            http_method: method
          },
          extra: {
            request_body: bodyRaw,
            response: response,
            error_details: response.error
          }
        });
        throw error;
      }

      // Handle case where response has a message property
      if (response.message) {
        const error = new Error(
          `${responseRaw.status}: ${JSON.stringify(response.message)}`,
          { cause: response }
        );
        void captureException(error, {
          user: await getUser(baseUrl),
          tags: {
            api_endpoint: parsedUrl.url,
            http_status: responseRaw.status,
            http_method: method
          },
          extra: {
            request_body: bodyRaw,
            response: response
          }
        });
        throw error;
      }

      // Handle case where response has a detail property
      if (response.detail) {
        const error = new Error(
          `${responseRaw.status}: ${JSON.stringify(response.detail)}`,
          { cause: response }
        );
        void captureException(error, {
          tags: {
            api_endpoint: parsedUrl.url,
            http_status: responseRaw.status,
            http_method: method
          },
          extra: {
            request_body: bodyRaw,
            response: response
          }
        });
        throw error;
      }

      // Fallback to the raw response as the message
      const error = new Error(
        `${responseRaw.status}: ${JSON.stringify(
          response || responseRaw.statusText
        )}`,
        { cause: response }
      );
      void captureException(error, {
        user: await getUser(baseUrl),
        tags: {
          api_endpoint: parsedUrl.url,
          http_status: responseRaw.status,
          http_method: method
        },
        extra: {
          request_body: bodyRaw,
          response: response || responseRaw.statusText
        }
      });
      throw error;
    }

    // Handle 204 No Content or empty body responses
    const contentLength = responseRaw.headers.get("content-length");
    if (responseRaw.status === 204 || contentLength === "0") {
      return {} as Response;
    }

    const text = await responseRaw.text();
    if (!text) {
      return {} as Response;
    }
    const response = JSON.parse(text);

    if (["staging", "production"].includes(getEnv())) {
      const result = await responseSchema.safeParseAsync(response);
      if (!result.success) {
        console.error({ zodError: z.prettifyError(result.error) });

        await captureException(result.error, {
          tags: {
            error_type: "schema_validation",
            api_endpoint: parsedUrl.url,
            http_method: method
          },
          extra: {
            validation_errors: z.prettifyError(result.error),
            response: response
          }
        });
      }

      return response as Response;
    }
    const parsed = await responseSchema.parseAsync(response);
    return parsed;
  } catch (error) {
    if (error instanceof ZodError) {
      void captureException(error, {
        tags: {
          error_type: "schema_validation",
          api_endpoint: url,
          http_method: method
        },
        extra: {
          validation_errors: z.prettifyError(error),
          request_body: bodyRaw
        }
      });
      throw new Error(z.prettifyError(error));
    }
    if (error instanceof Error) {
      // Network errors or other fetch errors
      if (
        error.message.includes("fetch") ||
        error.message.includes("network")
      ) {
        void captureException(error, {
          tags: {
            error_type: "network",
            api_endpoint: url,
            http_method: method
          },
          extra: {
            request_body: bodyRaw,
            error_message: error.message
          }
        });
      }
      throw error;
    }
    // Unknown error
    const unknownError = new Error("Unknown error");
    void captureException(unknownError, {
      tags: {
        error_type: "unknown",
        api_endpoint: url,
        http_method: method
      },
      extra: {
        request_body: bodyRaw,
        original_error: error
      }
    });
    throw unknownError;
  }
};

// make global window type for SERVER_BASE_URL
declare global {
  interface Window {
    SERVER_BASE_URL?: string;
  }
}
